import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { MetricCard } from '@/components/common/MetricCard';
import { StatusBadge, ChannelBadge, RiskBadge } from '@/components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  MessageSquareText,
  Reply,
  CalendarCheck,
  Building2,
  ChevronRight,
  Clock,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  BarChart3,
  TrendingUp
} from 'lucide-react';

export const DashboardPage = () => {
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [propertyStats, setPropertyStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, jobsRes, propStatsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/review-jobs?limit=5'),
        api.get('/dashboard/property-stats')
      ]);
      setStats(statsRes.data);
      setRecentJobs(Array.isArray(jobsRes.data) ? jobsRes.data : []);
      setPropertyStats(Array.isArray(propStatsRes.data) ? propStatsRes.data : []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleSeedDemo = async () => {
    try {
      await api.post('/demo/seed');
      toast.success('Données de démonstration créées');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création des données');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <Header title="Tableau de bord" />
        <div className="p-6 flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-pulse-soft text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  const pendingReviews = (stats?.jobs_by_status?.draft_ready || 0) + (stats?.jobs_by_status?.needs_human_review || 0);
  const guestReviews = stats?.jobs_by_type?.guest_review || 0;
  const hostReplies = stats?.jobs_by_type?.host_reply || 0;

  return (
    <MainLayout>
      <Header
        title="Tableau de bord"
        subtitle="Vue d'ensemble de vos avis et réponses"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="dashboard">
        {/* Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="À traiter"
            value={pendingReviews}
            subtitle="Avis et réponses en attente"
            icon={Clock}
          />
          <MetricCard
            title="Avis voyageurs"
            value={guestReviews}
            subtitle="Total des avis générés"
            icon={MessageSquareText}
          />
          <MetricCard
            title="Réponses hôte"
            value={hostReplies}
            subtitle="Total des réponses"
            icon={Reply}
          />
          <MetricCard
            title="Propriétés"
            value={stats?.total_properties || 0}
            subtitle={`${stats?.total_bookings || 0} réservations`}
            icon={Building2}
          />
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-[#FF5A6E]" />
                Avis voyageurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                {stats?.jobs_by_status?.draft_ready || 0} brouillons prêts
              </p>
              <Link to="/guest-reviews">
                <Button variant="outline" className="w-full text-[#FF5A6E] border-[#FF5A6E] hover:bg-[#FFF0F1]">
                  Voir tout <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Revue humaine
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                {stats?.jobs_by_status?.needs_human_review || 0} en attente de validation
              </p>
              <Link to="/guest-reviews?status=needs_human_review">
                <Button variant="outline" className="w-full text-amber-600 border-amber-300 hover:bg-amber-50">
                  Examiner <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)] transition-shadow">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Publiés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                {stats?.jobs_by_status?.published || 0} publiés récemment
              </p>
              <Link to="/guest-reviews?status=published">
                <Button variant="outline" className="w-full text-emerald-600 border-emerald-300 hover:bg-emerald-50">
                  Historique <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Property response stats */}
        {propertyStats.length > 0 && (
          <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[#FF5A6E]" />
                Taux de réponse par propriété
              </CardTitle>
              <span className="text-xs text-slate-400">{propertyStats.length} propriétés avec activité</span>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(propertyStats || []).slice(0, 8).map((prop) => (
                  <div
                    key={prop.property_id}
                    className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                    data-testid={`property-stat-${prop.property_id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      prop.channel === 'airbnb' ? 'bg-[#FF5A5F]/10' : prop.channel === 'booking' ? 'bg-[#0071C2]/10' : 'bg-slate-100'
                    }`}>
                      <span className={`font-bold text-sm ${
                        prop.channel === 'airbnb' ? 'text-[#FF5A5F]' : prop.channel === 'booking' ? 'text-[#0071C2]' : 'text-slate-500'
                      }`}>
                        {prop.channel === 'airbnb' ? 'A' : prop.channel === 'booking' ? 'B' : 'D'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm truncate">{prop.property_name}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-slate-400">{prop.total_jobs} jobs</span>
                        <span className="text-xs text-emerald-500">{prop.published} publiés</span>
                        {prop.guest_reviews > 0 && <span className="text-xs text-violet-500">{prop.guest_reviews} avis</span>}
                        {prop.host_replies > 0 && <span className="text-xs text-cyan-500">{prop.host_replies} réponses</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            prop.response_rate >= 80 ? 'bg-emerald-500' : prop.response_rate >= 50 ? 'bg-amber-400' : 'bg-slate-300'
                          }`}
                          style={{ width: `${Math.min(prop.response_rate, 100)}%` }}
                        />
                      </div>
                      <span className={`text-sm font-semibold min-w-[3rem] text-right ${
                        prop.response_rate >= 80 ? 'text-emerald-600' : prop.response_rate >= 50 ? 'text-amber-600' : 'text-slate-500'
                      }`}>
                        {prop.response_rate}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent activity */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base font-semibold">Activité récente</CardTitle>
            {recentJobs.length === 0 && (
              <Button
                onClick={handleSeedDemo}
                variant="outline"
                size="sm"
                className="text-[#2E86DE] border-[#2E86DE] hover:bg-[#EAF4FC]"
                data-testid="seed-demo-btn"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                Charger données démo
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {recentJobs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <p>Aucune activité récente</p>
                <p className="text-sm mt-1">Synchronisez Beds24 ou créez des données de test</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(recentJobs || []).map((job) => (
                  <Link
                    key={job.id}
                    to={`/review/${job.id}`}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100"
                    data-testid={`job-item-${job.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        job.job_type === 'guest_review' ? 'bg-violet-100' : 'bg-cyan-100'
                      }`}>
                        {job.job_type === 'guest_review' ? (
                          <MessageSquareText className="h-5 w-5 text-violet-600" />
                        ) : (
                          <Reply className="h-5 w-5 text-cyan-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{job.guest_name}</p>
                        <p className="text-xs text-slate-500">{job.property_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <ChannelBadge channel={job.channel} />
                      <StatusBadge status={job.status} />
                      <RiskBadge level={job.risk_level} />
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default DashboardPage;
